import { ANALYSIS_SECTION } from '@/lib/copy/analysis'

/**
 * Regras de query string da secao unificada (spec r6/29, R1).
 *
 * Modulo puro, sem JSX, pelo mesmo motivo de `navItems.ts`: a coercao da aba e a
 * preservacao dos filtros do redirect sao testaveis no vitest em ambiente node, antes
 * de qualquer componente existir.
 */
export type AnalysisTab = 'guia' | 'progresso'

/**
 * Ordem das abas na tela (r7-01): Analise de Progresso primeiro, Guia Estatistico depois.
 * A pagina renderiza os `TabsTrigger` nesta ordem, e a primeira e a padrao — pedido de
 * Pedro e Otavio na MH-R07 (a r6-29 abria no guia).
 */
export const ANALYSIS_TAB_ORDER: readonly AnalysisTab[] = ['progresso', 'guia']

// A lista trava a query string; o tipo `AnalysisTab` trava o JSX.
const VALID_TABS: readonly string[] = ANALYSIS_TAB_ORDER

/**
 * A aba que abre sem `?tab=` (entrada do menu, URL crua). Desde a r7-01 e a Analise de
 * Progresso. Como o padrao fica FORA da URL, todo link que quer o Guia precisa mandar
 * `?tab=guia` — use `buildAnalysisHref('guia')`, nunca `ANALYSIS_SECTION.path` cru.
 */
export const ANALYSIS_DEFAULT_TAB: AnalysisTab = 'progresso'

/** Converte qualquer string (query string, link antigo) numa aba valida. */
export function parseAnalysisTab(value: string | null | undefined): AnalysisTab {
  return value && VALID_TABS.includes(value) ? (value as AnalysisTab) : ANALYSIS_DEFAULT_TAB
}

/** `?tab=` da aba: vazio para a padrao, `tab=<aba>` para as outras. Parte de uma copia. */
function withTab(search: string, tab: AnalysisTab): string {
  const params = new URLSearchParams(search)

  if (tab === ANALYSIS_DEFAULT_TAB) params.delete('tab')
  else params.set('tab', tab)

  const query = params.toString()
  return query ? `?${query}` : ''
}

/**
 * Href de um link interno para uma aba da secao (r7-01). E a UNICA forma de apontar para
 * uma aba especifica: a aba padrao fica fora da URL, entao um link que dependesse da
 * ausencia de `?tab=` mudaria de destino sozinho a cada troca de padrao.
 */
export function buildAnalysisHref(tab: AnalysisTab): string {
  return `${ANALYSIS_SECTION.path}${withTab('', tab)}`
}

export interface AnalysisTarget {
  pathname: string
  search: string
}

/**
 * Destino do redirect de uma das duas URLs antigas.
 *
 * Preserva a query INTEIRA — nao so `?period=`, `?status=`, `?specialty=` e `?q=` que a
 * spec cita, mas tambem `?aberto=` (temas expandidos em ThemesProgressTable) e qualquer
 * parametro que apareca depois. `<Navigate to="/app/analise-estatistica" />` descartaria
 * tudo isso em silencio; por isso o destino e um objeto `{ pathname, search }`.
 *
 * A aba padrao fica FORA da URL, a mesma disciplina de /app/analise-metricas: URL curta
 * para o caso comum, e nenhum estado duplicado entre o parametro e o padrao.
 */
export function buildLegacyAnalysisTarget(search: string, tab: AnalysisTab): AnalysisTarget {
  return { pathname: ANALYSIS_SECTION.path, search: withTab(search, tab) }
}
